import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Logo } from '@/components/Logo';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { InstallButton, isStandalone } from '@/components/InstallPrompt';
import { RideRating } from '@/components/RideRating';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { useT } from '@/lib/i18n';
import { VEHICLE_LABELS, fmtDate } from '@/lib/format';
import { Play, Navigation, ChevronRight, Users, CheckCircle2, Crown, ShieldCheck } from 'lucide-react';

export default function Home() {
  const navigate = useNavigate();
  const t = useT();
  const user = useApp((s) => s.user);
  const activeRideId = useApp((s) => s.activeRideId);
  const setActiveRideId = useApp((s) => s.setActiveRideId);
  const pendingRating = useApp((s) => s.pendingRating);
  const setPendingRating = useApp((s) => s.setPendingRating);
  const familyMode = useApp((s) => s.familyMode);
  const [rides, setRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);

  useEffect(() => {
    api.listRides().then((list) => {
      setRides(list);
      const active = list.find((r) => r.status === 'active' || r.status === 'safety_mode');
      if (active) { setActiveRide(active); setActiveRideId(active.id); }
      else if (activeRideId && !active) setActiveRideId(null);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const greeting = user ? `${t('h_hi')}, ${(user.display_name || 'friend').split(' ')[0]}` : t('h_welcome');

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-5">
        <Logo />
        <button onClick={() => navigate('/app/account')} className="w-10 h-10 rounded-full flex items-center justify-center border" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }} aria-label="Account">
          {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full" /> : <Users size={20} style={{ color: 'var(--text-muted)' }} />}
        </button>
      </div>

      <h1 className="text-2xl font-extrabold" style={{ color: 'var(--text)' }}>{greeting}</h1>
      <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>{t('h_ready')}</p>

      {activeRide ? (
        <button data-testid="home-resume-ride-button" onClick={() => navigate(`/app/ride/${activeRide.id}`)} className="w-full text-left rounded-2xl p-4 mb-4 border ss-pulse" style={{ background: 'var(--surface)', borderColor: 'var(--primary)', boxShadow: 'var(--shadow)' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold" style={{ color: 'var(--primary)' }}>{t('h_ride_active')}</div>
              <div className="font-bold" style={{ color: 'var(--text)' }}>{t('h_return')}</div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{activeRide.public_ride_id}</div>
            </div>
            <ChevronRight style={{ color: 'var(--primary)' }} />
          </div>
        </button>
      ) : (
        <button data-testid="home-start-ride-button" onClick={() => navigate('/app/start')} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl font-bold min-h-[64px] text-lg mb-4 transition-colors" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)', boxShadow: 'var(--shadow)' }}>
          <Play size={24} /> {t('h_start')}
        </button>
      )}

      {familyMode && (
        <div className="rounded-2xl p-4 mb-4 border flex items-center justify-between" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <Crown size={18} style={{ color: user?.subscription_tier === 'family' ? 'var(--primary)' : 'var(--text-muted)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{user?.subscription_tier === 'family' ? t('h_family') : t('h_free')}</span>
          </div>
          <button onClick={() => navigate('/app/account')} className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>{t('h_manage')}</button>
        </div>
      )}

      {familyMode && user?.subscription_tier === 'family' && (
        <button
          data-testid="home-family-dashboard-button"
          onClick={() => navigate('/app/family')}
          className="w-full text-left rounded-2xl p-4 mb-4 border flex items-center justify-between transition-colors"
          style={{ background: 'var(--surface)', borderColor: 'var(--primary)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--primary) 14%, transparent)' }}>
              <Users size={20} style={{ color: 'var(--primary)' }} />
            </div>
            <div>
              <div className="text-sm font-bold" style={{ color: 'var(--text)' }}>{t('h_family_dash_t')}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('h_family_dash_b')}</div>
            </div>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} />
        </button>
      )}

      <button
        data-testid="home-check-vehicle-button"
        onClick={() => navigate('/app/vehicle')}
        className="w-full text-left rounded-2xl p-4 mb-4 border flex items-center justify-between transition-colors"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--surface-alt)' }}>
            <ShieldCheck size={20} style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <div className="text-sm font-bold" style={{ color: 'var(--text)' }}>{t('h_check_t')}</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('h_check_b')}</div>
          </div>
        </div>
        <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} />
      </button>

      <div className="flex items-center justify-between mb-2">
        <h2 className="font-bold" style={{ color: 'var(--text)' }}>{t('h_recent')}</h2>
        <button onClick={() => navigate('/app/rides')} className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>{t('h_seeall')}</button>
      </div>
      <div className="space-y-2" data-testid="home-ride-history-list">
        {rides.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('h_norides')}</p>}
        {rides.slice(0, 3).map((r) => (
          <button key={r.id} onClick={() => navigate(`/app/ride/${r.id}`)} className="w-full text-left rounded-2xl p-3 border flex items-center justify-between" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-3">
              {r.status === 'completed' ? <CheckCircle2 size={20} style={{ color: 'var(--success)' }} /> : <Navigation size={20} style={{ color: 'var(--primary)' }} />}
              <div>
                <div className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{VEHICLE_LABELS[r.vehicle_type] || 'Ride'} · {r.public_ride_id}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmtDate(r.started_at)}</div>
              </div>
            </div>
            <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} />
          </button>
        ))}
      </div>

      {!isStandalone() && (
        <div className="rounded-2xl p-4 mt-4 border" style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)' }}>
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{t('h_install_t')}</p>
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{t('h_install_b')}</p>
          <InstallButton className="w-full" label={t('l_install')} />
          <button onClick={() => navigate('/install')} className="w-full mt-2 text-xs font-semibold" style={{ color: 'var(--primary)' }} data-testid="home-install-guide-link">{t('h_install_guide')}</button>
        </div>
      )}

      <div className="mt-5"><ThemeSwitcher compact /></div>

      <RideRating
        open={!!pendingRating}
        ride={pendingRating}
        onClose={() => setPendingRating(null)}
        onSubmitted={() => setPendingRating(null)}
      />
    </AppShell>
  );
}
